import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ShoppingBag,
  User,
  PlusCircle,
  Trash2,
  Calculator,
  Loader2,
  Search,
  CircleDollarSign,
} from "lucide-react";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [stock, setStock] = useState([]);
  const [newSale, setNewSale] = useState({
    distributorId: "",
    product: "",
    quantity: "",
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPerPage = 10;

  useEffect(() => {
    fetchSales();
    fetchDistributors();
    fetchStock();
  }, []);

  const fetchSales = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/sales`);
      setSales(res.data);
    } catch {
      toast.error("Failed to load sales data");
    }
  };

  const fetchDistributors = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/distributors`);
      setDistributors(res.data);
    } catch {
      toast.error("Failed to fetch distributors");
    }
  };

  const fetchStock = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/stock`);
      setStock(res.data);
    } catch {
      toast.error("Failed to fetch stock data");
    }
  };

  const getBV = (productName) => {
    const product = stock.find((p) => p.name === productName);
    return product ? product.bv : 0;
  };

  const getStockColor = (qty) => {
    if (qty >= 20) return null;
    if (qty > 10 && qty <= 20) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  // const handleAddSale = async () => {
  //   if (!newSale.distributorId || !newSale.product || !newSale.quantity) {
  //     return toast.error("⚠️ Please fill all fields!");
  //   }

  //   const qty = parseInt(newSale.quantity, 10);
  //   if (isNaN(qty) || qty <= 0) return toast.error("Enter a valid quantity.");

  //   const selectedProduct = stock.find((p) => p.name === newSale.product);
  //   if (!selectedProduct) return toast.error("Product not found in stock.");

  //   if (qty > selectedProduct.quantity)
  //     return toast.error(`❌ Only ${selectedProduct.quantity} left in stock.`);

  //   const bv = selectedProduct.bv;
  //   const payload = {
  //     distributorId: newSale.distributorId,
  //     product: newSale.product,
  //     quantity: qty,
  //     bv,
  //   };

  //   setLoading(true);
  //   try {
  //     await axios.post(`${SERVER_URL}/api/sales`, payload);
  //     await axios.patch(`${SERVER_URL}/api/stock/decrement`, {
  //       name: newSale.product,
  //       quantity: qty,
  //     });

  //     toast.success("✅ Sale recorded successfully!");
  //     fetchSales();
  //     fetchStock();

  //     const updatedStock = selectedProduct.quantity - qty;
  //     if (updatedStock <= 0)
  //       toast.error(`${newSale.product} is now out of stock!`);
  //     else if (updatedStock <= 5)
  //       toast(`Low stock: only ${updatedStock} left.`, {
  //         icon: "⚠️",
  //         style: { background: "#fff3cd", color: "#856404" },
  //       });

  //     setNewSale({ distributorId: "", product: "", quantity: "" });
  //   } catch {
  //     toast.error("❌ Failed to record sale or update stock.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const handleAddSale = async () => {
    if (!newSale.distributorId || !newSale.product || !newSale.quantity) {
      return toast.error("⚠️ Please fill all fields!");
    }

    const qty = parseInt(newSale.quantity, 10);
    if (isNaN(qty) || qty <= 0) return toast.error("Enter a valid quantity.");

    const selectedProduct = stock.find((p) => p.name === newSale.product);
    if (!selectedProduct) return toast.error("Product not found in stock.");

    if (qty > selectedProduct.quantity)
      return toast.error(`❌ Only ${selectedProduct.quantity} left in stock.`);

    // 🧭 Find full distributor object
    const distributor = distributors.find(
      (d) => d._id === newSale.distributorId
    );
    if (!distributor) return toast.error("Distributor not found.");

    // ✅ Determine price based on distributor name
    const isNonMember = distributor.name === "Non-Member";
    const price = isNonMember
      ? selectedProduct.retailPrice
      : selectedProduct.distributorPrice;

    const totalPrice = price * qty;
    const bv = selectedProduct.bv;
    const totalBV = bv * qty;

    const payload = {
      distributorId: newSale.distributorId,
      product: newSale.product,
      quantity: qty,
      bv,
      totalBV,
      price,
      totalPrice,
    };

    setLoading(true);
    try {
      await axios.post(`${SERVER_URL}/api/sales`, payload);
      await axios.patch(`${SERVER_URL}/api/stock/decrement`, {
        name: newSale.product,
        quantity: qty,
      });

      toast.success("✅ Sale recorded successfully!");
      fetchSales();
      fetchStock();

      const updatedStock = selectedProduct.quantity - qty;
      if (updatedStock <= 0)
        toast.error(`${newSale.product} is now out of stock!`);
      else if (updatedStock <= 5)
        toast(`Low stock: only ${updatedStock} left.`, {
          icon: "⚠️",
          style: { background: "#fff3cd", color: "#856404" },
        });

      setNewSale({ distributorId: "", product: "", quantity: "" });
    } catch (err) {
      console.error("Sale Error:", err.response?.data || err.message);
      toast.error("❌ Failed to record sale or update stock.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this sale?")) {
      try {
        await axios.delete(`${SERVER_URL}/api/sales/${id}`);
        toast.success("Sale deleted!");
        fetchSales();
      } catch {
        toast.error("Failed to delete sale");
      }
    }
  };

  const filteredSales = sales.filter(
    (s) =>
      s.distributor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.product.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSales.length / recordsPerPage);
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentSales = filteredSales.slice(indexOfFirst, indexOfLast);

  const totalBVSum = filteredSales.reduce(
    (acc, sale) => acc + (sale.totalBV || 0),
    0
  );

  return (
    <div className="pt-24 px-6 md:px-12 min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="flex items-center justify-between mb-10">
        <h1 className="text-3xl font-bold text-green-800 flex items-center gap-2">
          <ShoppingBag size={30} className="text-green-700" /> Sales Management
        </h1>
      </div>

      {/* Add Sale */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-10 max-w-4xl mx-auto border border-green-100">
        <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-2">
          <PlusCircle size={20} className="text-green-600" /> Record New Sale
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={newSale.distributorId}
            onChange={(e) =>
              setNewSale({ ...newSale, distributorId: e.target.value })
            }
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
          >
            <option value="">Select Distributor</option>
            {distributors.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </select>
          {/* <select
              value={newSale.distributorId}
              onChange={(e) =>
                setNewSale({ ...newSale, distributorId: e.target.value })
              }
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select Distributor</option>
              <option value="non-member">Non-Member</option> 
              {distributors.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name}
                </option>
              ))}
            </select> */}

          <select
            value={newSale.product}
            onChange={(e) =>
              setNewSale({ ...newSale, product: e.target.value })
            }
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
          >
            <option value="">Select Product</option>
            {stock.map((p) => (
              <option key={p._id} value={p.name} disabled={p.quantity <= 0}>
                {p.name} —{" "}
                {p.quantity <= 0 ? "Out of stock" : `${p.quantity} left`}
              </option>
            ))}
          </select>

          <input
            type="number"
            placeholder="Quantity"
            value={newSale.quantity}
            onChange={(e) =>
              setNewSale({ ...newSale, quantity: e.target.value })
            }
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
          />

          <button
            onClick={handleAddSale}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg py-2 flex items-center justify-center"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              "Add Sale"
            )}
          </button>
        </div>

        {newSale.product && newSale.quantity && (
          <div className="mt-4 flex items-center gap-2 text-green-700">
            <Calculator size={18} />
            <p>
              Estimated BV:{" "}
              <span className="font-bold">
                {getBV(newSale.product) * parseInt(newSale.quantity)} BV
              </span>
            </p>
          </div>
        )}
        {newSale.product && newSale.quantity && (
          <div className="mt-4 flex items-center gap-2 text-green-700">
            <CircleDollarSign size={18} />
            <p>
              Estimated Price:{" "}
              <span className="font-bold">
                {(() => {
                  const selectedProduct = stock.find(
                    (p) => p.name === newSale.product
                  );
                  if (!selectedProduct) return "—";

                  const qty = parseInt(newSale.quantity, 10);
                  if (isNaN(qty) || qty <= 0) return "—";

                  const distributor = distributors.find(
                    (d) => d._id === newSale.distributorId
                  );

                  // ✅ Only check if distributor name is "Non-Member"
                  const isNonMember = distributor?.name === "Non-Member";

                  const price = isNonMember
                    ? selectedProduct.retailPrice
                    : selectedProduct.distributorPrice;

                  return `${price * qty} KES`;
                })()}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Stock Overview */}
      <div className="bg-white shadow-sm rounded-2xl p-6 mb-10 border border-green-100">
        <h2 className="text-lg font-semibold text-green-700 mb-4">
          Low & Warning Stock Levels
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {stock
            .filter((item) => getStockColor(item.quantity)) // 👈 only show reds & oranges
            .map((item) => (
              <div
                key={item._id}
                className={`p-3 rounded-xl shadow-sm border text-sm flex justify-between items-center ${getStockColor(
                  item.quantity
                )}`}
              >
                <span>{item.name}</span>
                <span className="font-semibold">{item.quantity}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-lg mx-auto flex items-center border border-green-300 rounded-lg px-4 py-2">
        <Search size={18} className="text-green-700" />
        <input
          type="text"
          placeholder="Search by distributor or product..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ml-2 w-full outline-none text-gray-700"
        />
      </div>

      {/* Sales Table */}
      <div className="bg-white shadow-md rounded-2xl p-6 border border-green-100">
        <h2 className="text-xl font-semibold text-green-700 mb-6 flex items-center gap-2">
          <User size={20} className="text-green-600" /> Distributor Sales
          Records
        </h2>

        {currentSales.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No matching records.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-green-100 text-green-800 text-left">
                  <th className="py-3 px-4 rounded-tl-lg">Distributor</th>
                  <th className="py-3 px-4">Product</th>
                  <th className="py-3 px-4">Quantity</th>
                  <th className="py-3 px-4">Total Price</th>
                  <th className="py-3 px-4">Total BV</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4 rounded-tr-lg text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentSales.map((sale) => (
                  <tr
                    key={sale._id}
                    className="border-b hover:bg-green-50 transition-all"
                  >
                    <td className="py-3 px-4">
                      {sale.distributor?.name || "N/A"}
                    </td>
                    <td className="py-3 px-4">{sale.product}</td>
                    <td className="py-3 px-4">{sale.quantity}</td>
                    <td className="py-3 px-4 font-semibold text-green-700">
                      {sale.totalPrice} KES
                    </td>
                    <td className="py-3 px-4 font-semibold text-green-700">
                      {sale.totalBV}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(sale.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleDelete(sale._id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-6">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg disabled:opacity-40"
            >
              Prev
            </button>
            <p className="text-green-700 font-medium">
              Page {currentPage} of {totalPages}
            </p>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}

        {sales.length > 0 && (
          <div className="mt-6 text-right">
            <p className="text-green-800 font-semibold text-lg">
              Total BV Awarded:{" "}
              <span className="text-green-700">{totalBVSum}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sales;
